import { mapGetters } from 'vuex';
import get from 'lodash/get';

import { LocalStorageKeys } from '@/enums/local-storage';
import { TagColors } from '@/components/tag/tag.enums';
import { RnTag, RnButton, RnActionHeader, RnSpinner, RnNewTable, RnTabs, RnFilters, RnField, RnMenu, RnPagination } from '@/components';
import { batteriesMixin } from '@/mixins';
import { ITEMS_PER_PAGE } from '@/services/constants';
import { formatAsDate } from '@/utils/format-as-date';
import { formatWeight } from '@/utils/units';
import { downloadAttachment } from '@/utils';

export default {
	name: 'rn-cp-collections-view',
	components: {
		RnPagination,
		RnButton,
		RnActionHeader,
		RnSpinner,
		RnNewTable,
		RnTabs,
		RnFilters,
		RnField,
		RnMenu,
	},
	mixins: [batteriesMixin],
	data() {
		return {
			ITEMS_PER_PAGE: ITEMS_PER_PAGE.DEFAULT,
			currentTabIndex: 0,
			ownerCollectionOrdersPage: 0,
			incomingUnplannedCollectionOrdersPage: 0,
			incomingPlannedCollectionOrdersPage: 0,
			visibleHeaders: [],
		};
	},
	computed: {
		...mapGetters([
			'ownerCollectionOrders',
			'ownerCollectionOrdersCount',
			'incomingUnplannedCollectionOrders',
			'incomingUnplannedCollectionOrdersCount',
			'incomingPlannedCollectionOrders',
			'incomingPlannedCollectionOrdersCount',
			'businessRelationId',
			'locations',
			'collectionOrderTransportPackagings',
		]),
		locationIds: () => (this.locations || []).map(location => location.id),
		collectionTableHeaders() {
			return [
				{
					text: this.$t('table.order.type'),
					value: 'orderType',
					compact: true,
					display: type => {
						return this.$t(`table.order.type.${type}`);
					},
				},
				{
					text: this.$t('table.order.status'),
					value: 'status',
					component: RnTag,
					compact: true,
					componentProps: value => {
						if (!value.status) {
							return null;
						}

						return {
							text: this.$t(`table.order.status.${value.status}`),
							color: TagColors[value.status.toLowerCase()],
							remark: value.deliveryRemarks,
						};
					},
				},
				{
					text: this.$t('table.order.number'),
					value: 'number',
					compact: true,
				},
				{
					text: '',
					component: RnButton,
					sortable: false,
					renderCondition: item => this.renderPlanCollection(item) || this.renderUpdateCollection(item),
					componentProps: item => ({
						color: 'blue',
						method: () => {
							if (this.renderPlanCollection(item)) {
								this.planCollection(item.id);
							} else if (this.renderUpdateCollection(item)) {
								this.updateCollection(item.id);
							}
						},
						outline: true,
						disableWrap: true,
						size: 'x-small',
						text: this.renderPlanCollection(item) ? this.$t('common.planCollection') : this.$t('common.updateCollection'),
					}),
				},
				{
					text: '',
					component: RnButton,
					sortable: false,
					renderCondition: item => this.renderUpdateCollection(item),
					componentProps: item => ({
						color: 'blue',
						method: () => this.checkCollection(item.id),
						outline: true,
						disableWrap: true,
						size: 'x-small',
						text: this.$t('common.checkCollection'),
					}),
				},
				{
					text: this.$t('table.order.dateRequested'),
					value: 'orderCreationDate',
					customSort: 'date',
					display: formatAsDate,
					compact: true,
				},
				{
					text: this.$t('table.order.dateCollection'),
					value: 'pickUpDate',
					customSort: 'date',
					display: formatAsDate,
					compact: true,
				},
				{
					text: this.$t('table.order.datePlanned'),
					value: 'plannedPickUpDate',
					customSort: 'date',
					display: formatAsDate,
					compact: true,
				},
				{
					text: this.$t('table.order.brand'),
					value: 'batteries',
					display: values => [...new Set(values?.map(value => value.batteryDescription.brand.name))]?.join(', '),
					compact: true,
					hideDetail: true,
				},
				{
					text: this.$t('table.order.collectedBy'),
					value: 'owner.name',
				},
				{
					text: this.$t('common.pickupPoint'),
					value: 'sourceLocation.name',
				},
				{
					text: this.$t('table.order.destinationLocation'),
					value: 'destinationLocation.name',
					compact: true,
				},
				{
					text: this.$t('common.totalWeight'),
					value: 'batteries',
					compact: true,
					customSort: batteries => (batteries || []).reduce((acc, battery) => acc + battery.nettoWeight, 0),
					display: batteries => {
						return formatWeight((batteries || []).reduce((acc, battery) => acc + battery.nettoWeight, 0));
					},
				},
				{
					text: this.$t('common.deliveryNote'),
					value: 'deliveryRemarks',
					compact: true,
					// for now, the only attachment on collection level is the packing slip.
					// if this changes in the future we need to refactor all related code.
					renderCondition: item => item.attachments && !!item.attachments.length,
					sortable: false,
					component: RnMenu,
					componentProps: value => ({
						icon: 'attachment-pdf',
						position: 'right',
						options: [
							{
								title: this.$t('common.deliveryNote'),
								iconRight: 'download',
								callback: () => downloadAttachment(value.packingSlipId, 'message.deliveryOrder.error', this.$store),
							},
						],
					}),
					detailComponent: RnButton,
					detailComponentProps: value => ({
						icon: { name: 'download', pos: 'right', width: '16px', height: '16px' },
						text: this.$t('common.downloadDeliveryNote'),
						color: 'blue',
						outline: true,
						size: 'small',
						method: () => downloadAttachment(value.packingSlipId, 'message.deliveryOrder.error', this.$store),
					}),
				},
			];
		},
		headerOptions() {
			return this.collectionTableHeaders
				.map(header => ({
					value: header.value,
					name: header.text,
				}))
				.filter(header => !!header.value);
		},
		batteryTableHeaders() {
			return this.nestedCategorizedBatteryTableHeaders;
		},
		tabs() {
			return [
				{
					label: this.$t('cp.collections.allOrders'),
					orders: this.ownerCollectionOrders,
					page: this.ownerCollectionOrdersPage,
					updatePage: this.updateOwnerCollectionOrders,
					totalItems: this.ownerCollectionOrdersCount,
				},
				{
					label: this.$t('cp.collections.incomingOrders'),
					orders: this.incomingUnplannedCollectionOrders,
					page: this.incomingUnplannedCollectionOrdersPage,
					updatePage: this.updateIncomingUnplannedCollectionOrders,
					totalItems: this.incomingUnplannedCollectionOrdersCount,
				},
				{
					label: this.$t('cp.collections.plannedTransportation'),
					orders: this.incomingPlannedCollectionOrders,
					page: this.incomingPlannedCollectionOrdersPage,
					updatePage: this.updateIncomingPlannedCollectionOrders,
					totalItems: this.incomingPlannedCollectionOrdersCount,
				},
			];
		},
		currentlyActiveTabInfo() {
			return get(this, `tabs[${this.currentTabIndex}]`);
		},
		collectionOrdersState() {
			if (!this.currentlyActiveTabInfo.orders) {
				return 'loading';
			}
			if (this.currentlyActiveTabInfo.orders.length) {
				return 'results';
			}
			return 'noResults';
		},
	},
	mounted() {
		const savedDisabledHeaders = JSON.parse(window.localStorage.getItem(LocalStorageKeys.HiddenHeaders)) ?? [];

		// Remove all disabled headers from all collectionTableHeaders
		this.visibleHeaders = this.collectionTableHeaders.map(header => header.value).filter(header => !savedDisabledHeaders.includes(header));
	},
	updated() {
		// Get the missing headers by comparing the visibleHeaders with all collectionTableHeaders
		const disabledHeaders = this.collectionTableHeaders.map(header => header.value).filter(header => this.visibleHeaders.indexOf(header) == -1);

		window.localStorage.setItem(LocalStorageKeys.HiddenHeaders, JSON.stringify(disabledHeaders));
	},
	created() {
		this.updateOwnerCollectionOrders(0);
		this.updateIncomingUnplannedCollectionOrders(0);
		this.updateIncomingPlannedCollectionOrders(0);
	},
	methods: {
		renderButtonLabel(record) {
			switch (record.status) {
				case 'Planned':
					return this.$t('common.updateCollection');

				default:
					return this.$t('common.planCollection');
			}
		},
		renderPlanCollection(record) {
			switch (record.status) {
				case 'Planned':
				case 'Executed':
				case 'ExecutedWithRemarks':
					return false;
				default:
					return true;
			}
		},
		renderUpdateCollection(record) {
			if (record.status === 'Planned') {
				return true;
			}

			return false;
		},
		updateCurrentTabIndex(index) {
			this.currentTabIndex = index;
		},
		formatRemarks: collectionOrder => {
			const content = [];
			if (get(collectionOrder, 'sourceRemarks')) {
				content.push(`<b>${collectionOrder.sourceBusinessRelation.name}</b><br/>${collectionOrder.sourceRemarks}`);
			}
			if (get(collectionOrder, 'destinationRemarks')) {
				content.push(`<b>${collectionOrder.destinationBusinessRelation.name}</b><br/>${collectionOrder.destinationRemarks}`);
			}

			return content.join('<br/>');
		},
		updateCollectionOrders(page) {
			this.updateOwnerCollectionOrders(page);
			this.updateIncomingUnplannedCollectionOrders(page);
			this.updateIncomingPlannedCollectionOrders(page);
		},
		handleExportToExcel() {
			switch (this.currentTabIndex) {
				case 0:
					this.updateOwnerCollectionOrders(0, true);
					break;
				case 1:
					this.updateIncomingUnplannedCollectionOrders(0, true);
					break;
				case 2:
					this.updateIncomingPlannedCollectionOrders(0, true);
					break;
			}
		},
		updateOwnerCollectionOrders(page, excel = false) {
			this.ownerCollectionOrdersPage = page;
			this.$store.dispatch('getOwnerCollectionOrders', {
				businessRelationId: this.businessRelationId,
				page: page || 0,
				pageSize: this.ITEMS_PER_PAGE,
				excel,
			});
		},
		updateIncomingUnplannedCollectionOrders(page, excel = false) {
			this.incomingUnplannedCollectionOrdersPage = page;
			this.$store.dispatch('getIncomingUnplannedCollectionOrders', {
				businessRelationId: this.businessRelationId,
				page: page || 0,
				pageSize: this.ITEMS_PER_PAGE,
				excel,
			});
		},
		updateIncomingPlannedCollectionOrders(page, excel = false) {
			this.incomingPlannedCollectionOrdersPage = page;
			this.$store.dispatch('getIncomingPlannedCollectionOrders', {
				businessRelationId: this.businessRelationId,
				page: page || 0,
				pageSize: this.ITEMS_PER_PAGE,
				excel,
			});
		},
		planCollection(collectionOrderId) {
			this.$router.push({ name: 'CPNewCollection', params: { collectionOrderId } });
		},
		updateCollection(collectionOrderId) {
			this.$router.push({ name: 'CPUpdateCollection', params: { collectionOrderId } });
		},
		checkCollection(collectionOrderId) {
			this.$router.push({ name: 'CPOrderChecking', params: { id: collectionOrderId } });
		},
		updateHeaders(value) {
			this.visibleHeaders = value;
		},
	},
};
